import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
// import { EntityList } from '@shared/model/api-get-interface';
import { IdentityFacade } from '../../application-services/identity.facade';
import { webApiUrl } from '@domains/shared/domain/utils/url';
import { headers } from '@domains/shared/domain/utils/global-constants';
import { BackofficeCompanyCourseDTO } from '@domains/course/domain/models/company-course.interface';
import { CertificateDTO } from '@domains/course/domain/models/certificate.dto';
import { ReleaseAccessOptionsDTO, ReleaseAccessPreviewDTO, ReleaseAccessRequestDTO } from '@domains/course/domain/models/release-access.dto';
import { CompanySectionDTO, SaveCompanySectionDTO } from '../../models/backoffice/company-section.interface';
import { ReminderResult, reminderResultOf } from '../../models/reminder-result.interface';


@Injectable({ providedIn: 'root' })
export class BackofficeCompanyCourseService {

  private baseUrl = `${webApiUrl}/backoffice/CompanyCourse`;

  constructor(
    private http: HttpClient,
    private identityFacade: IdentityFacade) {
  }

  updateCompanyCourse(companyCourse: BackofficeCompanyCourseDTO): Observable<BackofficeCompanyCourseDTO> {
    const url = `${this.baseUrl}/${companyCourse.id}`;

    return this.http.put<BackofficeCompanyCourseDTO>(url, companyCourse, { headers });
  }

  /**
   * Firemní sekce kurzu i s textem, který k nim tahle firma napsala (EL-2046).
   * Jazyky licence chodí všechny, i nevyplněné.
   */
  getCompanySections(companyCourseId: string): Observable<CompanySectionDTO[]> {
    const url = `${this.baseUrl}/${companyCourseId}/company-section`;
    return this.http.get<CompanySectionDTO[]>(url, { headers });
  }

  saveCompanySection(companyCourseId: string, data: SaveCompanySectionDTO): Observable<void> {
    const url = `${this.baseUrl}/${companyCourseId}/company-section`;
    return this.http.put<void>(url, data, { headers });
  }

  getCompanyCourse(courseId: string): Observable<BackofficeCompanyCourseDTO> {
    const url = `${this.baseUrl}/${courseId}`;
    return this.http.get<BackofficeCompanyCourseDTO>(url, { headers });
  }

  /**
   * Rozešle připomínku studentům, kteří kurz nedokončili.
   * Vrací počet skutečně odeslaných — komu notifikace nechodí, ten ji nedostane.
   */
  sendReminder(companyCourseId: string, url: string = `${this.baseUrl}/${companyCourseId}/Reminder`): Observable<ReminderResult> {
    return this.http.post<number>(url, {}, { headers, observe: 'response' }).pipe(map(reminderResultOf));
  }

  /**
   * Rozešle připomínku uživatelům přiřazeným do kurzu, kteří nedokončili registraci.
   * Vrací počet skutečně odeslaných.
   */
  sendReminderUnregistered(companyCourseId: string, url: string = `${this.baseUrl}/${companyCourseId}/Reminder/unregistered`): Observable<ReminderResult> {
    return this.http.post<number>(url, {}, { headers, observe: 'response' }).pipe(map(reminderResultOf));
  }

  /**
   * Přeplánuje neotevřené články zapsaných studentů podle současného nastavení kurzu
   * (EL-2352). Server práci zařadí do fronty a vrátí 202 bez těla.
   */
  recomputeAccess(companyCourseId: string, url: string = `${this.baseUrl}/${companyCourseId}/recompute-access`): Observable<void> {
    return this.http.post<void>(url, {}, { headers });
  }

  /** Články kurzu v chronologickém pořadí a počet zapsaných studentů (EL-2355). */
  getReleaseAccessOptions(companyCourseId: string, url: string = `${this.baseUrl}/${companyCourseId}/release-access/options`): Observable<ReleaseAccessOptionsDTO> {
    return this.http.get<ReleaseAccessOptionsDTO>(url, { headers });
  }

  /** Kolika studentům by zpřístupnění „až po článek" něco otevřelo (EL-2355). */
  previewReleaseAccess(
    companyCourseId: string,
    articleId: string,
    onlyBehind: boolean,
    url: string = `${this.baseUrl}/${companyCourseId}/release-access/preview`,
  ): Observable<ReleaseAccessPreviewDTO> {
    return this.http.get<ReleaseAccessPreviewDTO>(url, { headers, params: { articleId, onlyBehind } });
  }

  /**
   * Zpřístupní dotčeným studentům články až po zvolený (EL-2355). Server práci zařadí
   * do fronty jako přepočet otevírání a vrátí 202 bez těla.
   */
  releaseAccess(
    companyCourseId: string,
    request: ReleaseAccessRequestDTO,
    url: string = `${this.baseUrl}/${companyCourseId}/release-access`,
  ): Observable<void> {
    return this.http.post<void>(url, request, { headers });
  }

  // TODO: StudentModel
  addUser(companyCourseId: string, userId: string): Observable<any> {
    const url = `${this.baseUrl}/${companyCourseId}/student`;
    // TODO: StudentModel
    return this.http.post<any>(url, { userId }, { headers });
  }

  /**
   * Nahraje jazykovou variantu certifikátu firemního kurzu. Vrací události kvůli
   * ukazateli průběhu. ⚠ Bez sdílených `headers` — `FormData` si hlavičku skládá sám.
   */
  uploadCertificate(
    companyCourseId: string,
    languageIso: string,
    file: File,
    url: string = `${this.baseUrl}/${companyCourseId}/certificate/${languageIso}`,
  ): Observable<HttpEvent<CertificateDTO>> {
    const form = new FormData();
    form.append('model', file);

    return this.http.post<CertificateDTO>(url, form, { reportProgress: true, observe: 'events' });
  }

  // Maže jazykovou variantu certifikátu firemního kurzu; neutrální certifikát
  // (endpoint .../Certificate bez jazyka) je fallback a zůstává beze změny.
  deleteCertificate(companyCourseId: string, languageIso: string): Observable<void> {
    const url = `${this.baseUrl}/${companyCourseId}/Certificate/${languageIso}`;
    return this.http.delete<void>(url, { headers });
  }

  // Jazyky, pro které už je nahraná per-jazyk šablona — pro stav „nahráno" v UI.
  getCertificateLanguages(companyCourseId: string): Observable<string[]> {
    const url = `${this.baseUrl}/${companyCourseId}/certificateLanguages`;
    return this.http.get<string[]>(url, { headers });
  }

}
